import { useState, useEffect } from "react";

export interface SubTab {
	name: string;
	current: boolean;
	content: React.ReactNode;
}

export interface TabsProps {
	initialSubTabs: SubTab[];
	ariaLabel: string;
	containerClass?: string;
	tabPadding?: string;
	forceActiveTabName?: string; //for when auto-switch on initial load changes the tabs
	onTabChange?: (tabName: string) => void;
}

export default function Tabs({
	initialSubTabs,
	containerClass,
	tabPadding,
	ariaLabel,
	forceActiveTabName,
	onTabChange,
}: TabsProps) {
	const [tabs, setTabs] = useState(initialSubTabs);

	// Sync tabs when initialSubTabs changes, but preserve the currently active tab
	useEffect(() => {
		setTabs((prevTabs) => {
			const activeTabName = forceActiveTabName ?? prevTabs.find((t) => t.current)?.name;
			return initialSubTabs.map((tab) => ({
				...tab,
				current: activeTabName ? tab.name === activeTabName : tab.current,
			}));
		});
	}, [initialSubTabs, forceActiveTabName]);

	function handleSubTabChange(name: string): void {
		setTabs((prev) =>
			prev.map((tab) =>
				tab.name === name ? { ...tab, current: true } : { ...tab, current: false }
			)
		);

		// for the admin reports, keeping the tab across all pages
		onTabChange?.(name);
	}

	const activeTab = tabs.find((t) => t.current);
	return (
		<div className={`${containerClass ?? "mt-8 px-4"}`}>
			<div
				aria-label={ariaLabel}
				className="flex divide-x divide-gray-200 rounded-lg shadow-sm items-stretch ">
				{tabs.map((tab, tabIdx) => (
					<button
						onClick={() => handleSubTabChange(tab.name)}
						key={tab.name}
						aria-current={tab.current ? "page" : undefined}
						className={`${
							tab.current
								? "text-gray-900 !text-lg bg-gray-100 border-b-2 border-b-[var(--subtab-border)]"
								: "text-gray-500 hover:text-gray-700 border-b !border-b-transparent hover:bg-gray-100"
						} 
									${tabIdx === 0 ? "rounded-l-lg" : tabIdx === tabs.length - 1 ? "rounded-r-lg" : ""} 
									group flex-1 relative ${tabPadding ?? "p-4"} text-center text-sm font-medium hover:cursor-pointer focus:z-10`}>
						<span>{tab.name}</span>
					</button>
				))}
			</div>
			<div>{activeTab?.content}</div>
		</div>
	);
}
